/**
 * USE_SOUNDS.JS - Hook principal pour jouer des sons
 * 
 * Ce hook fournit l'API principale pour jouer des sons dans l'application.
 * Il gère toutes les optimisations (anti-spam, variations, vélocité).
 * 
 * FONCTIONNALITÉS:
 * - Lecture de sons simples
 * - Sons basés sur vélocité (collisions)
 * - Anti-spam (limitation de fréquence)
 * - Variations aléatoires (volume, pitch)
 * - Sélection aléatoire de variantes
 * 
 * API:
 * - play(name, options): Jouer un son simple
 * - playWithVelocity(name, velocity, options): Son avec vélocité
 * 
 * OPTIMISATIONS IMPLÉMENTÉES:
 * - Limitation de fréquence via minDelta
 * - Volume avec courbe quadratique
 * - Pitch aléatoire pour éviter répétition
 * - Validation des paramètres
 */

import { useCallback } from 'react'
import { useAudioStore } from '../store/useAudioStore'

/**
 * Hook useSounds
 * 
 * Fournit les fonctions pour jouer des sons dans l'application.
 * 
 * @returns {object} API du système audio
 * 
 * EXEMPLE D'UTILISATION:
 * ```javascript
 * function MyComponent() {
 *   const { play, playWithVelocity } = useSounds()
 *   
 *   const handleClick = () => {
 *     play('uiArea')
 *   }
 *   
 *   const handleCollision = (velocity) => {
 *     playWithVelocity('impact', velocity)
 *   }
 *   
 *   return <mesh onClick={handleClick}>...</mesh>
 * }
 * ```
 */
export function useSounds() {
  // Récupération des sons depuis le store
  const sounds = useAudioStore((state) => state.sounds)

  // ============================================
  // FONCTION PRINCIPALE DE LECTURE
  // ============================================

  /**
   * Jouer un son avec vélocité
   * 
   * Fonction interne qui gère toute la logique de lecture.
   * 
   * @param {string} name - Nom du son (clé dans SOUNDS_CONFIG)
   * @param {number} velocity - Vélocité pour calcul volume (0 par défaut)
   * @param {object} spatialParams - Paramètres spatiaux (volume, pan)
   * @returns {boolean} true si son joué, false sinon
   */
  const playSound = useCallback(
    (name, velocity = 0, spatialParams = {}) => {
      // ============================================
      // 1. VÉRIFICATIONS PRÉLIMINAIRES
      // ============================================

      // Vérifier existence du son
      const sound = sounds[name]
      if (!sound) {
        console.warn(`⚠️ Son "${name}" introuvable`)
        return false
      }

      // Temps actuel
      const currentTime = Date.now()

      // ============================================
      // 2. ANTI-SPAM (minDelta)
      // ============================================

      // Vérifier délai minimum entre lectures
      if (currentTime < sound.lastTime + sound.config.minDelta) {
        // Trop récent, on ignore
        return false
      }

      // ============================================
      // 3. VÉRIFICATION VÉLOCITÉ MINIMUM
      // ============================================

      // Si vélocité minimum définie, vérifier
      if (sound.config.velocityMin > 0 && velocity < sound.config.velocityMin) {
        // Vélocité insuffisante
        return false
      }

      // ============================================
      // 4. SÉLECTION VARIANTE ALÉATOIRE
      // ============================================

      // Sélectionner aléatoirement une instance Howl
      const randomIndex = Math.floor(Math.random() * sound.instances.length)
      const howlInstance = sound.instances[randomIndex]

      // ============================================
      // 5. CALCUL DU VOLUME
      // ============================================

      // Volume de base selon vélocité
      let volume = Math.min(
        Math.max(
          (velocity - sound.config.velocityMin) * sound.config.velocityMultiplier,
          sound.config.volumeMin
        ),
        sound.config.volumeMax
      )

      // Application d'une courbe quadratique (plus naturel)
      volume = Math.pow(volume, 2)

      // Application des paramètres spatiaux (si fournis)
      if (spatialParams.volume !== undefined) {
        volume *= spatialParams.volume
      }

      // Clamp final entre 0 et 1
      volume = Math.max(0, Math.min(1, volume))

      // ============================================
      // 6. CALCUL DU PITCH (RATE)
      // ============================================

      // Pitch aléatoire dans la plage définie
      const rateAmplitude = sound.config.rateMax - sound.config.rateMin
      const rate = sound.config.rateMin + Math.random() * rateAmplitude

      // ============================================
      // 7. APPLICATION DES PARAMÈTRES
      // ============================================

      howlInstance.volume(volume)
      howlInstance.rate(rate)

      // Panning spatial (si fourni)
      if (spatialParams.pan !== undefined) {
        howlInstance.stereo(spatialParams.pan)
      }

      // ============================================
      // 8. LECTURE DU SON
      // ============================================

      // Démarrer la lecture et récupérer l'ID du son
      const soundId = howlInstance.play()

      // ============================================
      // 9. SEEK (POSITION DE DÉPART)
      // ============================================

      // Si un temps de départ est spécifié, positionner le son à ce moment
      if (spatialParams.seek !== undefined) {
        howlInstance.seek(spatialParams.seek, soundId)
      }

      // ============================================
      // 10. SAUVEGARDE DU TEMPS
      // ============================================

      // Mémoriser le temps de lecture pour anti-spam
      sound.lastTime = currentTime

      return true
    },
    [sounds]
  )

  // ============================================
  // API PUBLIQUE
  // ============================================

  /**
   * Jouer un son simple
   * 
   * @param {string} name - Nom du son
   * @param {object} options - Options (spatialParams, etc.)
   * @returns {boolean} true si son joué
   * 
   * EXEMPLE:
   * ```javascript
   * play('uiArea')
   * play('horn', { spatialParams: { volume: 0.5, pan: -0.3 } })
   * play('mood1', { spatialParams: { seek: 15 } })  // Démarrer à 15 secondes
   * ```
   */
  const play = useCallback(
    (name, options = {}) => {
      return playSound(name, 0, options.spatialParams || {})
    },
    [playSound]
  )

  /**
   * Jouer un son avec vélocité
   * 
   * Utilisé pour les sons de collision où le volume dépend
   * de la force de l'impact.
   * 
   * @param {string} name - Nom du son
   * @param {number} velocity - Vélocité (force de l'impact)
   * @param {object} options - Options (spatialParams, etc.)
   * @returns {boolean} true si son joué
   * 
   * EXEMPLE:
   * ```javascript
   * // Dans un callback de collision
   * const velocity = e.contact.getImpactVelocityAlongNormal()
   * playWithVelocity('impact', velocity)
   * ```
   */
  const playWithVelocity = useCallback(
    (name, velocity, options = {}) => {
      return playSound(name, velocity, options.spatialParams || {})
    },
    [playSound]
  )

  /**
   * Arrêter un son
   * 
   * @param {string} name - Nom du son à arrêter
   * 
   * EXEMPLE:
   * ```javascript
   * stop('engine')
   * ```
   */
  const stop = useCallback(
    (name) => {
      const sound = sounds[name]
      if (!sound) {
        console.warn(`⚠️ Son "${name}" introuvable`)
        return
      }

      // Arrêter toutes les instances
      sound.instances.forEach((howl) => {
        howl.stop()
      })
    },
    [sounds]
  )

  /**
   * Mettre en pause un son
   * 
   * @param {string} name - Nom du son à mettre en pause
   */
  const pause = useCallback(
    (name) => {
      const sound = sounds[name]
      if (!sound) {
        console.warn(`⚠️ Son "${name}" introuvable`)
        return
      }

      // Mettre en pause toutes les instances
      sound.instances.forEach((howl) => {
        howl.pause()
      })
    },
    [sounds]
  )

  /**
   * Vérifier si un son est en train de jouer
   * 
   * @param {string} name - Nom du son
   * @returns {boolean} true si le son joue
   */
  const isPlaying = useCallback(
    (name) => {
      const sound = sounds[name]
      if (!sound) {
        return false
      }

      // Vérifier si au moins une instance est en train de jouer
      return sound.instances.some((howl) => howl.playing())
    },
    [sounds]
  )

  /**
   * Toggle un son (play si arrêté, stop si en cours)
   * 
   * @param {string} name - Nom du son
   * @param {object} options - Options (spatialParams, etc.)
   * @returns {boolean} true si le son joue après toggle
   * 
   * EXEMPLE:
   * ```javascript
   * toggle('mood1')  // Toggle normal
   * toggle('mood1', { spatialParams: { seek: 15 } })  // Démarrer à 15s si play
   * ```
   */
  const toggle = useCallback(
    (name, options = {}) => {
      if (isPlaying(name)) {
        stop(name)
        return false
      } else {
        play(name, options)
        return true
      }
    },
    [isPlaying, stop, play]
  )

  // ============================================
  // RETOUR DE L'API
  // ============================================

  return {
    play,                   // Jouer un son simple
    playWithVelocity,       // Jouer avec vélocité
    stop,                   // Arrêter un son
    pause,                  // Mettre en pause
    isPlaying,              // Vérifier si un son joue
    toggle                  // Toggle un son (play/stop)
  }
}

/**
 * NOTES D'UTILISATION:
 * 
 * 1. SONS SIMPLES (UI, effets):
 *    ```javascript
 *    const { play } = useSounds()
 *    play('uiArea')
 *    ```
 * 
 * 2. SONS AVEC VÉLOCITÉ (collisions):
 *    ```javascript
 *    const { playWithVelocity } = useSounds()
 *    playWithVelocity('impact', velocity)
 *    ```
 * 
 * 3. ANTI-SPAM:
 *    Configuré dans SOUNDS_CONFIG via minDelta.
 *    Le système ignore automatiquement si trop fréquent.
 * 
 * 4. VARIATIONS:
 *    - Plusieurs fichiers → sélection aléatoire
 *    - rateMin/Max → variation pitch aléatoire
 *    - volumeMin/Max → plage de volume
 * 
 * 5. SPATIALISATION:
 *    Utiliser useSpatialSound pour sons 3D.
 *    Ce hook gère uniquement la lecture de base.
 */

